import ROUTER from './ROUTER'

export interface TabItem {
  categoryID: number
  description: string
  isVistTab: boolean
  codeKey?: string
}

export interface MenuItemNode {
  key: string
  label: string
  path?: string
  icon?: string
  children?: MenuItemNode[]
}

// module = segment đầu codeKey ("Categories.BusinessField.View" → "Categories"), khớp cách
// FE legacy nhóm sidebar theo module (apps/fe/legacy/goods-trace-admin/src/app/pages/*)
const MODULE_LABEL: Record<string, string> = {
  Dashboards: 'Tổng quan',
  Categories: 'Danh mục dùng chung',
  Goods: 'Sản phẩm',
  Ecommerces: 'Thương mại điện tử',
  Enterprises: 'Doanh nghiệp',
  Partners: 'Đối tác',
  Productions: 'Sản xuất',
  Reports: 'Báo cáo - Thống kê',
  Settings: 'Cài đặt hệ thống',
  Traces: 'Truy xuất - Tem nhãn',
}

// tên icon lucide-react (render qua ICON_MAP ở layouts/index.tsx) — 1 icon/module, leaf đứng
// riêng (module chỉ 1 tab, không bọc group) thừa hưởng luôn icon module cho đồng bộ
const MODULE_ICON: Record<string, string> = {
  Dashboards: 'LayoutDashboard',
  Categories: 'FolderTree',
  Goods: 'Package',
  Ecommerces: 'ShoppingCart',
  Enterprises: 'Building2',
  Partners: 'Handshake',
  Productions: 'Factory',
  Reports: 'BarChart3',
  Settings: 'Settings',
  Traces: 'ScanLine',
}

// Phân nhóm module cho từng codeKey (vì codeKey mới từ backend không còn chứa tiền tố module)
const CODEKEY_MODULE: Record<string, string> = {
  BusinessField: 'Categories',
  Province: 'Categories',
  Ward: 'Categories',
  District: 'Categories',
  Unit: 'Categories',
  Organization: 'Categories',
  ProductCategory: 'Goods',

  DashboardAdmin: 'Dashboards',

  Order: 'Ecommerces',
  SupplyDemand: 'Ecommerces',

  Company: 'Enterprises',
  CompanyRegister: 'Enterprises',

  Product: 'Goods',
  ProductRegister: 'Goods',
  ProductPromotionRegister: 'Goods',
  ProductPromotion: 'Goods',
  ProductOcopRegister: 'Goods',
  ProductOcop: 'Goods',
  ProductEvaluationDossier: 'Goods',

  Partner: 'Partners', // Dự phòng legacy
  PartnerProducer: 'Partners',
  PartnerProcessor: 'Partners',
  PartnerLogistics: 'Partners',
  PartnerDistributor: 'Partners',

  HygieneSafety: 'Productions',
  ProductDiary: 'Productions',
  ProductSupplyChain: 'Productions',
  ProductionOrder: 'Productions',
  ProductionZone: 'Productions',
  ProductZone: 'Productions',

  ReportAdminCompany: 'Reports',
  ReportAdminProduct: 'Reports',
  ReportAdminQrCode: 'Reports',
  ReportEnterpriseProduct: 'Reports',
  ReportEnterpriseProductionOrder: 'Reports',
  ReportEnterpriseQrCode: 'Reports',
  ReportEnterpriseSaleOrder: 'Reports',
  ReportEnterpriseSupplyDemand: 'Reports',
  ReportEnterprisePartner: 'Reports',
  ReportEnterpriseCompany: 'Reports',
  ProductReportStatistic: 'Reports',
  QrCodeRegisterReport: 'Reports',

  GeneralSetting: 'Settings',
  CodeSetting: 'Settings',
  User: 'Settings',
  Employee: 'Settings',
  AuditLog: 'Settings',
  Menu: 'Settings',
  Role: 'Settings',
  Permission: 'Settings',
  SystemConnect: 'Settings',

  QrCode: 'Traces',
  QrCodeRegister: 'Traces',
}

// Map codeKey → route path
const CODEKEY_PATH: Record<string, string> = {
  BusinessField: ROUTER.LINH_VUC_HOAT_DONG,
  Province: ROUTER.TINH_THANH,
  District: ROUTER.QUAN_HUYEN,
  Ward: ROUTER.XA_PHUONG,
  Unit: ROUTER.DON_VI_DO_LUONG,
  CodeSetting: ROUTER.CAI_DAT_CHUNG_TIEN_TO,
  GeneralSetting: ROUTER.CAI_DAT_CHUNG_TIEN_TO,
  Organization: ROUTER.DON_VI_QUAN_LY,
  ProductCategory: ROUTER.DANH_MUC_SAN_PHAM,

  DashboardAdmin: ROUTER.THONG_KE_TONG_QUAN,
  // DashboardEnterprise: ROUTER.THONG_KE_TONG_QUAN,

  Order: ROUTER.DON_HANG,
  SupplyDemand: ROUTER.CUNG_CAU,

  Company: ROUTER.DOANH_NGHIEP,
  CompanyRegister: ROUTER.DANG_KY_DOANH_NGHIEP,

  Product: ROUTER.PRODUCT,
  ProductRegister: ROUTER.DANG_KY_SAN_PHAM,
  ProductPromotionRegister: ROUTER.DANG_KY_KHUYEN_MAI,
  ProductPromotion: ROUTER.PRODUCT_PROMOTION,
  ProductOcopRegister: ROUTER.PRODUCT_OCOP_REGISTER,
  ProductOcop: ROUTER.PRODUCT_OCOP,
  ProductEvaluationDossier: ROUTER.PRODUCT_EVALUATION_DOSSIER,

  Partner: ROUTER.DOI_TAC_SAN_XUAT,
  PartnerProcessor: ROUTER.DOI_TAC_SO_CHE,
  PartnerLogistics: ROUTER.DOI_TAC_KHO_VAN,
  PartnerDistributor: ROUTER.DOI_TAC_PHAN_PHOI,

  HygieneSafety: ROUTER.AN_TOAN_SAN_XUAT,
  ProductDiary: ROUTER.NHAT_KY_SAN_PHAM,
  ProductSupplyChain: ROUTER.CHUOI_CUNG_UNG,
  ProductionOrder: ROUTER.DON_SAN_XUAT,
  ProductionZone: ROUTER.VUNG_SAN_XUAT,
  ProductZone: ROUTER.VUNG_SAN_XUAT,

  ReportAdminCompany: ROUTER.BAO_CAO_DOANH_NGHIEP,
  ReportAdminProduct: ROUTER.BAO_CAO_SAN_PHAM,
  ReportAdminQrCode: ROUTER.THONG_KE_TEM_NHAN,
  ReportEnterpriseProduct: ROUTER.BAO_CAO_SAN_PHAM,
  ReportEnterpriseProductionOrder: ROUTER.THONG_KE_DON_SAN_XUAT,
  ReportEnterpriseQrCode: ROUTER.THONG_KE_TEM_NHAN_DOANH_NGHIEP,
  ReportEnterpriseSaleOrder: ROUTER.THONG_KE_DON_HANG,
  ReportEnterpriseSupplyDemand: ROUTER.THONG_KE_CUNG_CAU,
  ReportEnterprisePartner: ROUTER.BAO_CAO_DOI_TAC,
  ReportEnterpriseCompany: ROUTER.THONG_KE_DOANH_NGHIEP_DN,
  ProductReportStatistic: ROUTER.THONG_KE_SAN_PHAM,
  QrCodeRegisterReport: ROUTER.THONG_KE_TEM_NHAN_DOANH_NGHIEP,

  User: ROUTER.QUAN_LY_NGUOI_DUNG,
  Employee: ROUTER.QUAN_LY_NGUOI_DUNG,
  AuditLog: ROUTER.NHAT_KY_HE_THONG,
  Menu: ROUTER.QUAN_LY_MENU,
  Role: ROUTER.QUAN_LY_PHAN_QUYEN,
  // Permission: ROUTER.QUAN_LY_DANH_MUC_CHUC_NANG,
  SystemConnect: ROUTER.THIET_LAP_KET_NOI,

  QrCode: ROUTER.QUAN_LY_MA_TEM,
  QrCodeRegister: ROUTER.DANG_KY_MA_TEM,
}

// label thân thiện thay cho description thô từ BE
const CODEKEY_LABEL: Record<string, string> = {
  BusinessField: 'Lĩnh vực hoạt động',
  Province: 'Tỉnh/Thành phố',
  Ward: 'Xã/Phường',
  District: 'Quận/Huyện',
  Unit: 'Đơn vị đo lường',
  CodeSetting: 'Cài đặt chung',
  GeneralSetting: 'Cài đặt chung',
  Organization: 'Đơn vị quản lý',
  ProductCategory: 'Loại sản phẩm',

  DashboardAdmin: 'Bảng thông tin',
  // DashboardEnterprise: 'Bảng thông tin doanh nghiệp',

  Order: 'Quản lý đơn hàng',
  SupplyDemand: 'Quản lý cung cầu',

  Company: 'Doanh nghiệp TXNG',
  CompanyRegister: 'Đăng ký doanh nghiệp',

  Product: 'Sản phẩm',
  ProductRegister: 'Đăng ký sản phẩm',
  ProductPromotionRegister: 'Đăng ký khuyến mại',
  ProductPromotion: 'Khuyến mãi',
  ProductOcopRegister: 'Đăng ký sản phẩm OCOP',
  ProductOcop: 'Sản phẩm OCOP',
  ProductEvaluationDossier: 'Quản lý hồ sơ đánh giá sản phẩm',

  Partner: 'Đối tác sản xuất ban đầu',
  PartnerProcessor: 'Đối tác sơ chế - chế biến',
  PartnerLogistics: 'Đối tác kho vận',
  PartnerDistributor: 'Đối tác phân phối',

  HygieneSafety: 'Vệ sinh an toàn sản xuất',
  ProductDiary: 'Nhật ký sản phẩm',
  ProductSupplyChain: 'Chuỗi cung ứng sản phẩm',
  ProductionOrder: 'Lô sản phẩm',
  ProductionZone: 'Vùng sản xuất',
  ProductZone: 'Vùng sản xuất',

  ReportAdminCompany: 'Báo cáo doanh nghiệp',
  ReportAdminProduct: 'Báo cáo sản phẩm',
  ReportAdminQrCode: 'Thống kê tem nhãn',
  ReportEnterpriseProduct: 'Báo cáo sản phẩm',
  ReportEnterpriseProductionOrder: 'Thống kê đơn sản xuất',
  ReportEnterpriseQrCode: 'Thống kê tem nhãn (DN)',
  ReportEnterpriseSaleOrder: 'Thống kê đơn hàng',
  ReportEnterpriseSupplyDemand: 'Báo cáo cung cầu',
  ReportEnterprisePartner: 'Báo cáo đối tác',
  ReportEnterpriseCompany: 'Thống kê doanh nghiệp',
  ProductReportStatistic: 'Thống kê sản phẩm',
  QrCodeRegisterReport: 'Thống kê tem nhãn (DN)',

  Employee: 'Quản lý người dùng',
  AuditLog: 'Nhật ký hệ thống',
  Menu: 'Quản lý menu',
  Role: 'Nhóm quyền',
  Permission: 'Danh mục chức năng',
  SystemConnect: 'Thiết lập kết nối',

  QrCode: 'Quản lý mã/tem',
  QrCodeRegister: 'Đăng ký mã/tem',
}

const CODEKEY_ICON: Record<string, string> = {
  BusinessField: 'Activity',
  Province: 'MapPin',
  Ward: 'MapPin',
  District: 'MapPin',
  Unit: 'Scale',
  CodeSetting: 'Settings',
  GeneralSetting: 'Settings',
  Organization: 'Building',
  ProductCategory: 'ListTree',

  DashboardAdmin: 'LayoutDashboard',

  Order: 'ClipboardList',
  SupplyDemand: 'ArrowRightLeft',

  Company: 'Building2',
  CompanyRegister: 'FileSignature',

  Product: 'Package',
  ProductRegister: 'PackagePlus',
  ProductPromotionRegister: 'Tag',
  ProductPromotion: 'Tag',
  ProductOcopRegister: 'PackagePlus',
  ProductOcop: 'Star',
  ProductEvaluationDossier: 'Package',

  Partner: 'Handshake',
  PartnerProducer: 'Handshake',
  PartnerProcessor: 'Handshake',
  PartnerLogistics: 'Handshake',
  PartnerDistributor: 'Handshake',

  HygieneSafety: 'ShieldCheck',
  ProductDiary: 'BookOpen',
  ProductSupplyChain: 'Link',
  ProductionOrder: 'Factory',
  ProductionZone: 'Map',
  ProductZone: 'Map',

  ReportAdminCompany: 'FileSpreadsheet',
  ReportAdminProduct: 'FileBarChart',
  ReportAdminQrCode: 'QrCode',
  ReportEnterpriseProduct: 'BarChart3',
  ReportEnterpriseProductionOrder: 'TrendingUp',
  ReportEnterpriseQrCode: 'ScanLine',
  ReportEnterpriseSaleOrder: 'LineChart',
  ReportEnterpriseSupplyDemand: 'PieChart',
  ReportEnterprisePartner: 'BarChart3',
  ReportEnterpriseCompany: 'BarChart3',
  ProductReportStatistic: 'BarChart3',
  QrCodeRegisterReport: 'ScanLine',

  // User: 'Users',
  Employee: 'Users',
  AuditLog: 'ScrollText',
  Menu: 'Menu',
  Role: 'KeySquare',
  Permission: 'ListChecks',
  SystemConnect: 'Plug',

  QrCode: 'Ticket',
  QrCodeRegister: 'TicketPlus',
}

export function buildMenuFromTabs(tabs: TabItem[] | null | undefined): MenuItemNode[] {
  if (!tabs || tabs.length === 0) return []

  const resolveCodeKey = (t: TabItem): string | undefined => {
    if (t.codeKey) {
      if (CODEKEY_PATH[t.codeKey]) return t.codeKey
      const parts = t.codeKey.split('.')
      if (parts.length >= 2 && CODEKEY_PATH[parts[1]]) return parts[1]
      if (CODEKEY_PATH[parts[0]]) return parts[0]
    }
    if (t.description) {
      const cleaned = t.description.replace(/^Quyền\s+/, '').trim()
      if (CODEKEY_PATH[cleaned]) return cleaned
      const parts = cleaned.split('.')
      if (parts.length >= 2 && CODEKEY_PATH[parts[1]]) return parts[1]
      if (CODEKEY_PATH[parts[0]]) return parts[0]
    }
    return undefined
  }

  const allTabs = [...tabs]
  if (!allTabs.some((t) => t.codeKey === 'ProductEvaluationDossier')) {
    allTabs.push({
      categoryID: 99999,
      description: 'Quản lý hồ sơ đánh giá sản phẩm',
      isVistTab: true,
      codeKey: 'ProductEvaluationDossier',
    })
  }

  const resolvedLeaves: { tab: TabItem; codeKey: string }[] = []
  for (const t of allTabs) {
    if (!t.isVistTab) continue
    const key = resolveCodeKey(t)
    if (key && CODEKEY_PATH[key]) {
      resolvedLeaves.push({ tab: t, codeKey: key })
    }
  }

  if (resolvedLeaves.length === 0) return []

  const byModule = new Map<string, MenuItemNode[]>()
  const addedPaths = new Set<string>()

  for (const { tab, codeKey } of resolvedLeaves) {
    const path = CODEKEY_PATH[codeKey]
    if (addedPaths.has(path)) continue
    addedPaths.add(path)

    const module = CODEKEY_MODULE[codeKey] ?? 'Khác'
    const leaf: MenuItemNode = {
      key: tab.categoryID ? String(tab.categoryID) : codeKey,
      label: CODEKEY_LABEL[codeKey] ?? tab.description,
      path: path,
      icon: CODEKEY_ICON[codeKey] || MODULE_ICON[module],
    }
    const group = byModule.get(module)
    if (group) group.push(leaf)
    else byModule.set(module, [leaf])
  }

  // 1 tab duy nhất trong module → hiện thẳng, không bọc group thừa 1 lớp
  const orderedModules = [
    ...Object.keys(MODULE_LABEL),
    ...[...byModule.keys()].filter((m) => !(m in MODULE_LABEL)),
  ]
  return orderedModules
    .filter((m) => byModule.has(m))
    .flatMap((m) => {
      const children = byModule.get(m) ?? []
      if (children.length === 1) return children
      return [{ key: `group-${m}`, label: MODULE_LABEL[m] ?? m, icon: MODULE_ICON[m], children }]
    })
}

// dùng chung cho MainLayout (build pathByKey) và MenuUser (tìm route đích khi bấm nút truy cập)
export const flattenLeaves = (nodes: MenuItemNode[]): MenuItemNode[] =>
  nodes.flatMap((m) => (m.children ? flattenLeaves(m.children) : [m]))
